import type { ParsedStatement, ParsedTransaction, PaymentMethod } from "@paisa/types"
import * as XLSX from "xlsx"
import { parseAmount } from "../lib/parse-helpers"

/** Column names in ICICI iMobile/net-banking "OpTransactionHistory" savings export */
const COLUMNS = {
  sno: "S No.",
  valueDate: "Value Date",
  txnDate: "Transaction Date",
  chequeNo: "Cheque Number",
  remarks: "Transaction Remarks",
  withdrawal: "Withdrawal Amount(INR)",
  deposit: "Deposit Amount(INR)",
  balance: "Balance(INR)",
} as const

/**
 * Detect whether an xlsx file is an ICICI savings account statement.
 * ICICI's own export carries no bank name in the content — this column
 * signature (Withdrawal/Deposit Amount(INR) + Cheque Number) is distinctive
 * to its net-banking "OpTransactionHistory" export.
 */
export function isIciciSavings(rawContent: string): boolean {
  // The real header row sits well past the metadata preamble, so the generic
  // sniffer's `headers` param (first row with 4+ cells) usually lands on a
  // metadata row instead — match on the full rawContent text instead.
  const hasIciciMarker = /Transactions List/i.test(rawContent)
  const hasColumns = rawContent.includes(COLUMNS.withdrawal) && rawContent.includes(COLUMNS.deposit)
  return hasIciciMarker && hasColumns
}

/**
 * Parse an ICICI savings account XLSX statement.
 */
export function parseIciciSavings(data: ArrayBuffer): ParsedStatement {
  const workbook = XLSX.read(new Uint8Array(data), { type: "array" })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) throw new Error("No sheet found in workbook")

  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })

  const metadata = extractMetadata(rows)

  const headerIdx = findHeaderRow(rows)
  if (headerIdx === -1) throw new Error("Could not find column headers in ICICI statement")

  const headerRow = (rows[headerIdx] as unknown[]).map((c) => String(c ?? "").trim())
  const colMap = mapColumns(headerRow)

  const transactions: ParsedTransaction[] = []
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i] as unknown[]
    if (!row || row.length === 0) continue

    const txn = parseRow(row, colMap)
    if (txn) transactions.push(txn)
  }

  return {
    bank: "icici",
    accountType: "savings",
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Metadata extraction ──────────────────────────────────────────────────

interface StatementMetadata {
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
}

function extractMetadata(rows: unknown[][]): StatementMetadata {
  let accountLast4: string | null = null
  let periodStart: string | null = null
  let periodEnd: string | null = null

  for (let i = 0; i < Math.min(20, rows.length); i++) {
    const rowText = (rows[i] ?? []).map((c) => String(c ?? "")).join(" ")

    // "Account Number   181601000866 ( INR )  - K AYUSHI"
    const accountMatch = rowText.match(/Account Number\D+(\d+)/)
    if (accountMatch) {
      accountLast4 = accountMatch[1].slice(-4)
    }

    // "Transaction Date from  01,07,2026 to  27,09,2026"
    const periodMatch = rowText.match(
      /Transaction Date from\D*(\d{2},\d{2},\d{4})\s+to\s*(\d{2},\d{2},\d{4})/i,
    )
    if (periodMatch) {
      periodStart = parseIciciDate(periodMatch[1])
      periodEnd = parseIciciDate(periodMatch[2])
    }
  }

  return { accountLast4, periodStart, periodEnd }
}

// ── Column detection ─────────────────────────────────────────────────────

interface ColumnMap {
  txnDate: number
  remarks: number
  chequeNo: number
  withdrawal: number
  deposit: number
  balance: number
}

function findHeaderRow(rows: unknown[][]): number {
  for (let i = 0; i < Math.min(30, rows.length); i++) {
    const row = (rows[i] ?? []).map((c) => String(c ?? "").trim())
    if (row.includes(COLUMNS.withdrawal) && row.includes(COLUMNS.deposit)) {
      return i
    }
  }
  return -1
}

function mapColumns(headerRow: string[]): ColumnMap {
  const find = (name: string): number => {
    const idx = headerRow.indexOf(name)
    if (idx === -1) throw new Error(`Column "${name}" not found in ICICI statement`)
    return idx
  }

  return {
    txnDate: find(COLUMNS.txnDate),
    remarks: find(COLUMNS.remarks),
    chequeNo: find(COLUMNS.chequeNo),
    withdrawal: find(COLUMNS.withdrawal),
    deposit: find(COLUMNS.deposit),
    balance: find(COLUMNS.balance),
  }
}

// ── Row parsing ──────────────────────────────────────────────────────────

function parseRow(row: unknown[], colMap: ColumnMap): ParsedTransaction | null {
  const rawDate = String(row[colMap.txnDate] ?? "").trim()
  const date = parseIciciDate(rawDate)
  if (!date) return null

  const remarks = String(row[colMap.remarks] ?? "")
    .replace(/\s+/g, " ")
    .trim()
  if (!remarks) return null

  const debit = parseAmount(row[colMap.withdrawal])
  const credit = parseAmount(row[colMap.deposit])

  if (debit === 0 && credit === 0) return null

  const amount = debit > 0 ? debit : credit
  const type: "debit" | "credit" = debit > 0 ? "debit" : "credit"

  const chequeNo = String(row[colMap.chequeNo] ?? "").trim() || null
  const closingBalance = parseAmount(row[colMap.balance]) || null

  const parsed = parseIciciNarration(remarks)

  return {
    date,
    description: remarks,
    amount,
    type,
    referenceNumber: chequeNo,
    valueDate: null,
    closingBalance,
    paymentMethod: parsed.paymentMethod,
    extractedName: parsed.name,
    upiId: parsed.upiId,
    sourceCategory: null,
  }
}

// ── ICICI Narration Parser ───────────────────────────────────────────────

interface NarrationInfo {
  paymentMethod: PaymentMethod | null
  name: string | null
  upiId: string | null
}

/**
 * Parse ICICI narration formats:
 *   "UPI/Zepto/zeptoonline@yb/UPI/YES BANK L/654895783662/ICI0616.../"
 *   "UPI/MANOJ PANGING-paytmqr6p7ej2@ptys/UPI/YES BANK L/.../Sent using..."
 */
export function parseIciciNarration(narration: string): NarrationInfo {
  const upper = narration.toUpperCase()

  const upiMatch = narration.match(/^UPI\/([^/]+)\/([^/\s]+)/i)
  if (upiMatch) {
    return {
      paymentMethod: "upi",
      name: upiMatch[1].trim() || null,
      upiId: upiMatch[2].trim() || null,
    }
  }

  if (upper.includes("NEFT")) {
    return { paymentMethod: "neft", name: null, upiId: null }
  }
  if (upper.includes("RTGS")) {
    return { paymentMethod: "rtgs", name: null, upiId: null }
  }
  if (upper.includes("IMPS")) {
    return { paymentMethod: "imps", name: null, upiId: null }
  }
  if (upper.includes("ATM")) {
    return { paymentMethod: "atm", name: null, upiId: null }
  }
  if (/\bPOS\b/.test(upper) || upper.includes("PURCHASE")) {
    return { paymentMethod: "pos", name: null, upiId: null }
  }
  if (upper.includes("INTEREST") || upper.includes("INT.")) {
    return { paymentMethod: "other", name: "Interest", upiId: null }
  }

  return { paymentMethod: null, name: null, upiId: null }
}

// ── Utilities ────────────────────────────────────────────────────────────

/** Parse ICICI's "DD,MM,YYYY" date format → "YYYY-MM-DD" */
function parseIciciDate(raw: string): string | null {
  if (!raw) return null

  const match = raw.trim().match(/^(\d{2}),(\d{2}),(\d{4})$/)
  if (!match) return null

  return `${match[3]}-${match[2]}-${match[1]}`
}
